import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Button,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes } from "@mui/system";
import InciTable from "./InciTable";
import { BORDER, SEVERITIES, TEXT_PRIMARY, TEXT_SECONDARY, VERSIONS } from "../constants";

export const CHANGE_STYLES = {
  insert: { label: "נוסף", bg: "#dcfce7", color: "#166534" },
  delete: { label: "הוסר", bg: "#fee2e2", color: "#991b1b", strike: true },
  replace: { label: "הוחלף", bg: "#fcefd6", color: "#7a3e00" },
  move: { label: "שינוי מיקום", bg: "transparent", color: "inherit", dotted: true },
};

// Pulses a change the user jumped to from the corrections list
const focusPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  30%, 70% { box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.45); }
`;

export const CHANGE_LABELS = {
  insert: "נוסף",
  delete: "הוסר",
  replace: "הוחלף",
  move: "שינוי מיקום",
  rename: "שינוי שם",
  section_added: "סעיף חדש",
  section_removed: "סעיף שהוסר",
};
const CATEGORY_LABELS = { punctuation: "פיסוק", case: "אותיות גדולות/קטנות" };
const CONFIDENCE_LABELS = { medium: "ודאות בינונית בהתאמת הסעיף", low: "ודאות נמוכה בהתאמת הסעיף" };

const severityOf = (key) => SEVERITIES.find((s) => s.key === key);

export const describeFinding = (finding) => {
  const parts = [CHANGE_LABELS[finding.type] || finding.type];
  if (CATEGORY_LABELS[finding.category]) parts[0] += ` (${CATEGORY_LABELS[finding.category]})`;
  if (finding.type === "move") parts.push(`מקום ${finding.beforePosition} ← ${finding.afterPosition}`);
  if (finding.type === "replace" || finding.type === "rename") {
    parts.push(`"${finding.before}" ← "${finding.after}"`);
  }
  parts.push(`חומרה: ${severityOf(finding.severity)?.label}`);
  return parts.join(" · ");
};

const styleFor = (change) => {
  if (change === "move") return CHANGE_STYLES.move;
  if (change === "insert" || change === "section_added") return CHANGE_STYLES.insert;
  if (change === "delete" || change === "section_removed") return CHANGE_STYLES.delete;
  return CHANGE_STYLES.replace;
};

const CONTEXT_CHARS = 28;
const GAP = " … ";

/**
 * Keeps only the text around the changes of a section ("…" for the rest),
 * so the report shows where things changed without repeating whole
 * paragraphs. Windows are widened to whole words and merged when close.
 */
// Where a text fragment sits in the section, tolerating spacing differences
const locate = (full, fragment) => {
  if (!fragment) return -1;
  const exact = full.indexOf(fragment);
  if (exact >= 0) return exact;
  const lastWords = fragment.trim().split(/\s+/).slice(-2).join(" ");
  return lastWords ? full.indexOf(lastWords) : -1;
};

/**
 * `anchors` are the words just before each change; they locate changes
 * that left nothing marked on this side (a word added only in the other
 * version).
 */
export const condenseSegments = (segments, anchors = []) => {
  const full = segments.map((segment) => segment.text).join("");
  const ranges = [];
  let offset = 0;
  segments.forEach((segment) => {
    const start = offset;
    offset += segment.text.length;
    if (segment.findingId) ranges.push([start, offset]);
  });
  anchors.forEach((anchor) => {
    const at = locate(full, anchor);
    if (at >= 0) ranges.push([at, at + anchor.length]);
  });
  if (!ranges.length) return [];

  const windows = [];
  ranges
    .sort((p, q) => p[0] - q[0])
    .forEach(([start, end]) => {
      let from = Math.max(0, start - CONTEXT_CHARS);
      let to = Math.min(full.length, end + CONTEXT_CHARS);
      while (from > 0 && !/\s/.test(full[from - 1])) from -= 1;
      while (to < full.length && !/\s/.test(full[to])) to += 1;
      const last = windows[windows.length - 1];
      if (last && from <= last.to + 8) last.to = Math.max(last.to, to);
      else windows.push({ from, to });
    });

  const result = [];
  if (windows[0].from > 0) result.push({ text: "…" });
  windows.forEach((window, index) => {
    if (index > 0) result.push({ text: GAP });
    let position = 0;
    segments.forEach((segment) => {
      const start = position;
      const end = position + segment.text.length;
      position = end;
      const from = Math.max(start, window.from);
      const to = Math.min(end, window.to);
      if (from < to) result.push({ ...segment, text: segment.text.slice(from - start, to - start) });
    });
  });
  if (windows[windows.length - 1].to < full.length) result.push({ text: "…" });
  // Trim the spaces next to the cut points
  return result.map((segment, i) =>
    segment.findingId ? segment : { ...segment, text: i === 0 ? segment.text.trimStart() : segment.text },
  );
};

const HighlightedText = ({ segments, side, findingsById, visibleSeverities, focusedFindingIds, ltr }) => {
  if (!segments.length) {
    return (
      <Typography variant="body2" color="#94a3b8" fontStyle="italic">
        {side === "previous" ? "הסעיף לא קיים בגרסה הקודמת" : "הסעיף לא קיים בגרסה המעודכנת"}
      </Typography>
    );
  }
  const renderPiece = (segment, text, key) => {
    const finding = segment.findingId && findingsById.get(segment.findingId);
    if (!finding || !visibleSeverities.has(finding.severity)) {
      return <React.Fragment key={key}>{text}</React.Fragment>;
    }
    const style = styleFor(segment.change);
    const isLow = finding.severity === "low";
    return (
      <Tooltip key={key} title={describeFinding(finding)} placement="top" arrow>
        <Box
          component="span"
          data-finding={finding.id}
          sx={{
            animation: focusedFindingIds?.has(finding.id) ? `${focusPulse} 1.2s ease-in-out 2` : "none",
            bgcolor: style.bg,
            color: style.color,
            textDecoration: style.strike ? "line-through" : "none",
            borderBottom: style.dotted ? "2px dotted #667085" : "none",
            borderRadius: style.dotted ? 0 : "3px",
            px: "2px",
            py: "1px",
            fontWeight: isLow ? 500 : 600,
            outline: isLow && !style.dotted ? `1px dashed ${style.color}66` : "none",
            cursor: "help",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  // Each line keeps its own direction, so an English line in a Hebrew
  // section shows "Produced?" and not "?Produced"
  const lines = [[]];
  segments.forEach((segment, index) => {
    segment.text.split("\n").forEach((text, part) => {
      if (part > 0) lines.push([]);
      if (text) lines[lines.length - 1].push(renderPiece(segment, text, `${index}-${part}`));
    });
  });

  return (
    <Typography
      component="div"
      dir={ltr ? "ltr" : "rtl"}
      sx={{ fontSize: 15, lineHeight: 2, color: TEXT_PRIMARY, whiteSpace: "pre-wrap", textAlign: "start" }}
    >
      {lines.map((pieces, index) => (
        <React.Fragment key={index}>
          {index > 0 && "\n"}
          <span dir="auto">{pieces}</span>
        </React.Fragment>
      ))}
    </Typography>
  );
};

const EmptyCell = () => <Typography component="span" color="#94a3b8">—</Typography>;

// Spec 3.3 output: section, wording before, wording after, severity
const FindingsTable = ({ findings }) => {
  const cellSx = { fontSize: 14, verticalAlign: "top", borderBottom: `1px solid ${BORDER}` };
  const headSx = { ...cellSx, color: TEXT_SECONDARY, fontWeight: 600, bgcolor: "#fafbfc", whiteSpace: "nowrap" };
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headSx}>שינוי</TableCell>
          <TableCell sx={headSx}>נוסח לפני</TableCell>
          <TableCell sx={headSx}>נוסח אחרי</TableCell>
          <TableCell sx={headSx}>חומרה</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {findings.map((finding) => {
          const severity = severityOf(finding.severity);
          const isMove = finding.type === "move";
          return (
            <TableRow key={finding.id} sx={{ "&:last-of-type td": { borderBottom: 0 } }}>
              <TableCell sx={{ ...cellSx, whiteSpace: "nowrap", color: TEXT_SECONDARY }}>
                {CHANGE_LABELS[finding.type]}
                {CATEGORY_LABELS[finding.category] && (
                  <Typography variant="caption" display="block" color="#94a3b8">
                    {CATEGORY_LABELS[finding.category]}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ ...cellSx, color: "#991b1b" }}>
                <bdi>{isMove ? `${finding.before} (מקום ${finding.beforePosition})` : finding.before || <EmptyCell />}</bdi>
              </TableCell>
              <TableCell sx={{ ...cellSx, color: "#166534" }}>
                <bdi>{isMove ? `${finding.after} (מקום ${finding.afterPosition})` : finding.after || <EmptyCell />}</bdi>
              </TableCell>
              <TableCell sx={cellSx}>
                <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: severity?.bg, color: severity?.color, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {severity?.label}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const SeverityCounts = ({ findings }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
    {SEVERITIES.map(({ key, label, color, bg }) => {
      const count = findings.filter((f) => f.severity === key).length;
      if (!count) return null;
      return (
        <Box key={key} component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: bg, color, fontSize: 13, fontWeight: 600 }}>
          {count} {label}
        </Box>
      );
    })}
  </Box>
);

const ColumnTitle = ({ version }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: version.color }} />
    <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT_SECONDARY }}>
      {version.label}
    </Typography>
  </Box>
);

// `embedded` drops the card frame and title, for use inside a row that
// already shows the section name (the comparison accordion)
// `condensed` shows only the text around the changes, without the
// details table (used by the report)
const SectionComparison = ({ section, findingsById, visibleSeverities, focusedFindingIds, embedded, condensed }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const findings = section.findingIds.map((id) => findingsById.get(id)).filter(Boolean);
  const visibleFindings = findings.filter((finding) => visibleSeverities.has(finding.severity));
  const ltr = section.type === "inci";
  const anchors = condensed ? findings.map((finding) => finding.contextBefore).filter(Boolean) : [];
  const textProps = { findingsById, visibleSeverities, focusedFindingIds, ltr };

  return (
    <Paper
      elevation={0}
      sx={embedded ? { borderRadius: 0, bgcolor: "transparent" } : { borderRadius: 2, border: `1px solid ${BORDER}`, overflow: "hidden" }}
    >
      {embedded ? (
        CONFIDENCE_LABELS[section.confidence] && (
          <Typography sx={{ display: "block", px: { xs: 2, sm: 2.5 }, pt: 1, fontSize: 13, color: "#b45309" }}>
            {CONFIDENCE_LABELS[section.confidence]}
          </Typography>
        )
      ) : (
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          bgcolor: "#fafbfc",
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: TEXT_PRIMARY }}>
          {section.label}
        </Typography>
        {findings.length > 0 ? (
          <SeverityCounts findings={findings} />
        ) : (
          <Typography sx={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>
            ללא שינויים
          </Typography>
        )}
        {CONFIDENCE_LABELS[section.confidence] && (
          <Typography sx={{ marginInlineStart: "auto", fontSize: 13, color: "#b45309" }}>
            {CONFIDENCE_LABELS[section.confidence]}
          </Typography>
        )}
      </Box>
      )}
      {condensed && section.rows ? (
        // Ingredient lists read best as a table of the changed ingredients
        <Box sx={{ px: { xs: 1, sm: 1.5 }, py: 1 }}>
          <InciTable rows={section.rows.filter((row) => row.status !== "unchanged")} />
          <Typography sx={{ px: 1, pt: 1, fontSize: 13, color: TEXT_SECONDARY }}>
            {section.rows.filter((row) => row.status === "unchanged").length} רכיבים ללא שינוי לא מוצגים
          </Typography>
        </Box>
      ) : (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderInlineEnd: { md: `1px solid ${BORDER}` }, borderBottom: { xs: `1px solid ${BORDER}`, md: "none" } }}>
          <ColumnTitle version={VERSIONS.previous} />
          <HighlightedText segments={condensed ? condenseSegments(section.previous, anchors) : section.previous} side="previous" {...textProps} />
        </Box>
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <ColumnTitle version={VERSIONS.updated} />
          <HighlightedText segments={condensed ? condenseSegments(section.updated, anchors) : section.updated} side="updated" {...textProps} />
        </Box>
      </Box>
      )}
      {ltr && !condensed && visibleFindings.some((finding) => finding.type === "move") && (
        <Typography sx={{ px: { xs: 2, sm: 2.5 }, py: 1, fontSize: 13, color: TEXT_SECONDARY, borderTop: `1px solid ${BORDER}` }}>
          {visibleFindings.filter((finding) => finding.type === "move").length} רכיבים שינו מיקום ברשימה — מסומנים בקו מנוקד
        </Typography>
      )}
      {!condensed && visibleFindings.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
          <Button
            fullWidth
            onClick={() => setDetailsOpen((open) => !open)}
            endIcon={
              <ExpandMoreIcon sx={{ transform: detailsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
            }
            sx={{ justifyContent: "flex-start", px: { xs: 2, sm: 2.5 }, py: 1, color: TEXT_SECONDARY, fontWeight: 600, borderRadius: 0 }}
          >
            {section.rows
              ? `השוואת INCI מלאה (${section.rows.length} רכיבים)`
              : `פירוט שינויים (${visibleFindings.length})`}
          </Button>
          <Collapse in={detailsOpen} unmountOnExit>
            <Box sx={{ px: { xs: 1, sm: 1.5 }, pb: 1.5, overflowX: "auto" }}>
              {section.rows ? <InciTable rows={section.rows} /> : <FindingsTable findings={visibleFindings} />}
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
};

export default SectionComparison;
